<template>
  <section class="section speciesSearch">
    <div
      v-if="speciesDisplayList.length >= 10"
      class="container"
    >
      <div class="columns mb-1 is-multiline is-centered">
        <div class="column is-6-desktop is-12-tablet">
          <div class="searchForm rightButton">
            <input
              ref="searchInput"
              v-model="searchTerms.filter"
              type="text"
              :placeholder="$t('taxonomicTable.input.message')"
              :clear-all-filter="clearFilter"
              name="search"
              class="input"
              aria-label="search text"
            >
            <button
              aria-label="Search"
              @click="manualSearch"
            >
              <span
                class="svg search-dark-20"
              />
            </button>
          </div>
        </div>
      </div>

      <div class="filterToggle">
        <div class="columns">
          <div class="column is-12 filterToggleButton">
            <p @click="showAdvancedFilter = !showAdvancedFilter">
              <span class="advanceFilterHeader">
                {{ $t("taxonomicTable.filter") }}
                <span
                  class="icon icon-arrow"
                  :class="{'up': !showAdvancedFilter}"
                /></span>
            </p>
          </div>
        </div>
        <div
          v-if="showAdvancedFilter"
          id="filterExpand"
        >
          <div class="columns is-multiline filters is-centered">
            <div
              class="column"
              :class="tableHeaderStyle"
            >
              <custom-dropdown
                v-model="searchTerms.cname"
                :label="$t('taxonomicTable.columns.cname')"
                :show-alphabet="true"
              />
            </div>
            <div
              class="column"
              :class="tableHeaderStyle"
            >
              <custom-dropdown
                v-model="searchTerms.sname"
                :label="$t('taxonomicTable.columns.sname') "
                :show-alphabet="true"
              />
            </div>
            <div
              class="column"
              :class="tableHeaderStyle"
            >
              <custom-dropdown
                v-model="searchTerms.status"
                :label="$t('taxonomicTable.columns.status')"
                :options="taxonomicStatusOptions"
              />
            </div>
          </div>
          <div class="columns">
            <div class="column" />
          </div>
        </div>
        <div class="filterList">
          <div class="results">
            <span v-if="hasFilter">{{ $t("taxonomicTable.resultInfoWithTerms", {filteredRecordNo: filteredItemCount, totalRecordNo: totalItemCount}) }}</span>
            <span v-else>{{ $t("taxonomicTable.resultInfo", { totalRecordNo: totalItemCount }) }}</span>
          </div>
          <div class="tags">
            <template
              v-for="(key, index) in Object.keys(searchTerms)"
              :key="'filter' + index"
            >
              <span
                v-show="searchTerms[key]"
                class="tag is-light"
              >
                {{ $t("taxonomicTable.columns." + key) }}: {{ searchTerms[key] }}
                <button
                  class="delete is-small"
                  aria-label="Clear Filter"
                  @click="clearFilter(key)"
                />
              </span>
            </template>
          </div>
          <div
            v-show="hasFilter"
            class="clearAll"
            @click="clearAllFilter"
          >
            {{ $t("taxonomicTable.clearFilter") }}
          </div>
        </div>
      </div>
    </div>
    <section
      class="section searchResults speciesTable"
    >
      <div class="container">
        <div class="bodySearchResults">
          <div class="resultHeader">
            <div class="photo is-hidden-mobile" />
            <!-- this can be write as a loop -->
            <template
              v-for="(key, index) in tableColumns"
              :key="'header' + index"
            >
              <div :class="{[key]: true, 'is-hidden-mobile': key === 'status'}">
                <p
                  class="caps clickable"
                  @click="sortColumn(key)"
                >
                  <strong>{{ $t("taxonomicTable.columns." + key) }}</strong>
                  <span
                    v-if="sort.columnName === key"
                    :class="orderArrowStyle"
                  />
                  <span
                    v-else
                    class="svg sortable-20 ml-2"
                  />
                </p>
              </div>
            </template>
          </div>
          <div class="resultList">
            <div
              v-for="(item, $index) in speciesDisplayList"
              :key="$index"
              class="singleResult"
              @click="jumptoLink(item)"
            >
              <div class="photo is-hidden-mobile">
                <img
                  :src="profileImageUrlPrefix + item.sname"
                  alt="species profile"
                  class="rounded-1"
                >
              </div>
              <div class="cname">
                {{ item.cname }}
              </div>
              <div class="sname">
                <em>{{ item.sname }}</em>
              </div>
              <div class="status is-hidden-mobile">
                <a
                  class="tag widthLimit"
                  :class="{c4: item.status === 'Rediscovered For AB', c2: item.status === 'Rediscovered For Canada', c3: item.status === 'New To Canada', c6: item.status === 'New To Science', c7: item.status === 'New To AB'}"
                  aria-hidden="true"
                >{{ item.status }}</a>
              </div>
            </div>
            <!-- issue with inifinitely loading no-result slot
            when all results fit  in one api, it is shown by error, so this is a work around
            -->
            <div
              v-show="filteredRecordNo === 0 || speciesDisplayList.length === 0"
              class="noResult"
            >
              {{ $t("common.noResult") }}
            </div>
          </div>
        </div>
      </div>
    </section>
  </section>
</template>

<script setup>
/* this component hanldes the species table
    */
import { ref, onBeforeMount, computed } from 'vue';
import { taxonomicStatusOptions } from '@/config/index.ts';
import { getAllSpecies } from '@/services/data/species.ts';
import CustomDropdown from '@/components/dropdown/AdvancedCustomDropdown.vue';

const tableColumns = ['cname', 'sname', 'status'];
const searchTerms = ref({});
const speciesList = ref([]);
const sort = ref({});
const showAdvancedFilter = ref(false);

const props = defineProps({
  userInput: { type: Object, default: null }
});

const speciesGroup = computed(() => props.userInput?.speciesGroup);
const profileImageUrlPrefix = import.meta.env.VITE_APP_PHOTO_PROFILE_URL_PREFIX;

onBeforeMount(async () => {
  const data = await getAllSpecies(speciesGroup.value);
  // console.log(data);
  speciesList.value.push(...data);
  // console.log(speciesList.value);
//   speciesList.value.forEach((item, index) => {
//     if (item.jobTitle) {
//       const splitRoles = item.jobTitle.split(',');
//       item.roles = splitRoles.join(',\n');
//     }
//   });
});

const hasFilter = computed(() => {
  return Object.values(searchTerms.value).filter(x => x.length > 0).length > 0;
});
/* query and filter the full list */
const speciesDisplayList = computed(() => {
  // console.log(searchTerms.value.cname, searchTerms.value.cname == null);
  const localSearch = {};
  Object.keys(searchTerms.value).forEach((key) => {
    localSearch[key] = searchTerms.value[key].toUpperCase();
  });
  const { cname, sname, status, filter } = localSearch;
  const list = speciesList.value.filter(species => {
    // console.log(species?.cname, species?.cname.toUpperCase(), filter, { species });
    const check = (cname == null || species.cname.toUpperCase().startsWith(cname)) &&
    (sname == null || species.sname.toUpperCase().startsWith(sname)) &&
    (status == null || species.status === status) &&
    (filter == null || (
      species?.cname.toUpperCase().includes(filter) ||
      species?.sname.toUpperCase().includes(filter) ||
      species?.status.toUpperCase().includes(filter)
    ));
    return check;
  });

  // watch(speciesDisplayList, newList => {
  //   newList.forEach(item => {
  //   });
  // });

  const { columnName, isAscendance } = sort.value;

  list.sort((a, b) => {
    const r = ((a[columnName] || 'zzz') > (b[columnName] || 'zzz')) ? 1 : -1;
    return r * (isAscendance ? 1 : -1);
  });

  return list;
});

const sortColumn = (columnName) => {
  /* a new column, start from ascendance */
  if (sort.value.columnName !== columnName) {
    sort.value.columnName = columnName;
    sort.value.isAscendance = true;
  } else { /* switch sort order if click on same column */
    sort.value.isAscendance = !sort.value.isAscendance;
  }
};

const totalItemCount = computed(() => {
  return speciesList.value.length;
});

const filteredItemCount = computed(() => {
  return speciesDisplayList.value.length;
});

const orderArrowStyle = computed(() => {
  return { 'svg chevron-15 ml-2': sort.value.isAscendance, 'svg chevron-down-15 ml-2': !sort.value.isAscendance };
});

const clearFilter = (filterName) => {
  delete searchTerms.value[filterName];
};

const clearAllFilter = () => {
  searchTerms.value = {};
};
const jumptoLink = (item) => {
  const baseurl = import.meta.env.VITE_APP_SPP_URL;
  const stringWithHyphens = item.sname.replace(/\s+/g, '-');
  const url = baseurl + stringWithHyphens;
  // console.log(url);
  window.open(url, '_blank');
};

</script>

<style lang="scss">
// not scoped. have some issue with deep.
@import 'table.scss';
</style>
